import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { type AccountType } from "@/components/account-badge";
import { ThemeSelector } from "@/components/theme-selector";
import { PrivacyControls } from "@/components/privacy-controls";
import { getDigestSubscription, setDigestSubscription, type DigestScope, type DigestFrequency } from "@/lib/digest.functions";
import {
  NOTIFICATION_CATEGORIES,
  getNotificationPreferences,
  saveNotificationPreferences,
  type NotificationPreferences,
} from "@/lib/notification-preferences";
import { LogOut } from "lucide-react";

export const Route = createFileRoute("/settings")({
  head: () => ({ meta: [{ title: "Xamseen — Settings" }] }),
  component: SettingsPage,
});

const ACCOUNT_OPTIONS: { value: AccountType; label: string }[] = [
  { value: "citizen", label: "Citizen" },
  { value: "government", label: "Government Representative 🏛" },
  { value: "municipality", label: "Municipality 🏙" },
  { value: "ngo", label: "NGO 🌍" },
  { value: "journalist", label: "Journalist 📰" },
  { value: "emergency", label: "Emergency Service 🚨" },
  { value: "business", label: "Business 💼" },
  { value: "research", label: "Research Organization 🎓" },
];

function SettingsPage() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [digest, setDigest] = useState<{ scope: DigestScope; frequency: DigestFrequency } | null>(null);
  const [digestLoading, setDigestLoading] = useState(true);
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);

  useEffect(() => { if (profile) setForm(profile); }, [profile]);
  // Signed-out handling lives in AppShell's gate, which preserves the destination.
  useEffect(() => {
    if (!user) return;
    setDigestLoading(true);
    getDigestSubscription()
      .then((d) => setDigest(d))
      .finally(() => setDigestLoading(false));
    void getNotificationPreferences(user.id).then(setPrefs);
  }, [user]);

  const save = async () => {
    if (!user) return;
    setSaving(true);
    const { error: profileError } = await supabase.from("profiles").update({
      full_name: form.full_name, bio: form.bio, location: form.location,
      website: form.website, language: form.language, timezone: form.timezone,
      privacy_profile: form.privacy_profile, privacy_location: form.privacy_location,
      notif_email: form.notif_email, notif_push: form.notif_push,
      account_type: form.account_type ?? "citizen",
      office: form.office || null, jurisdiction: form.jurisdiction || null,
      agency: form.agency || null, official_website: form.official_website || null,
    }).eq("id", user.id);

    let digestError: any = null;
    if (digest) {
      try {
        await setDigestSubscription({ data: { scope: digest.scope, frequency: digest.frequency } });
      } catch (e) {
        digestError = e;
      }
    }

    let prefsError: any = null;
    if (prefs) {
      try {
        await saveNotificationPreferences(user.id, prefs);
      } catch (e) {
        prefsError = e;
      }
    }

    setSaving(false);
    if (profileError) toast.error(profileError.message);
    else if (digestError) toast.error(digestError?.message ?? "Digest save failed");
    else if (prefsError) toast.error(prefsError?.message ?? "Notification preferences save failed");
    else { toast.success("Settings saved"); refreshProfile(); }
  };

  if (!profile) return <AppShell><p className="text-muted-foreground">Loading…</p></AppShell>;

  const acct: AccountType = form.account_type ?? "citizen";
  const showOfficialFields = acct !== "citizen";

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl space-y-6">
        <h1 className="font-display text-3xl font-semibold tracking-tight">Settings</h1>

        <Card className="space-y-4 p-6">
          <h2 className="font-semibold">Profile</h2>
          <div><Label>Full name</Label><Input value={form.full_name ?? ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
          <div><Label>Bio</Label><Textarea value={form.bio ?? ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} /></div>
          <div><Label>Location</Label><Input value={form.location ?? ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City, Country" /></div>
          <div><Label>Website</Label><Input value={form.website ?? ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
        </Card>

        <Card className="space-y-4 p-6">
          <div>
            <h2 className="font-semibold">Account type</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Official accounts (government, municipality, NGO, journalist, emergency, business, research) get a verified badge on posts and comments. Verification is granted by moderators after review.
            </p>
          </div>
          <div>
            <Label>Account type</Label>
            <Select value={acct} onValueChange={(v) => setForm({ ...form, account_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACCOUNT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showOfficialFields && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Office / Title</Label><Input value={form.office ?? ""} onChange={(e) => setForm({ ...form, office: e.target.value })} placeholder={acct === "government" ? "Mayor, Councillor…" : "Director, Chief…"} /></div>
              <div><Label>Jurisdiction</Label><Input value={form.jurisdiction ?? ""} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} placeholder="City, State, Country" /></div>
              <div><Label>Agency / Organization</Label><Input value={form.agency ?? ""} onChange={(e) => setForm({ ...form, agency: e.target.value })} placeholder="Ministry, NGO, Outlet…" /></div>
              <div><Label>Official website</Label><Input value={form.official_website ?? ""} onChange={(e) => setForm({ ...form, official_website: e.target.value })} placeholder="https://" /></div>
            </div>
          )}
        </Card>

        <Card className="space-y-4 p-6">
          <div>
            <h2 className="font-semibold">Appearance</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Choose how Xamseen looks. System follows your device automatically and updates live when your device switches.
            </p>
          </div>
          <ThemeSelector />
        </Card>

        <Card className="space-y-4 p-6">
          <h2 className="font-semibold">Localization</h2>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Language</Label><Input value={form.language ?? "en"} onChange={(e) => setForm({ ...form, language: e.target.value })} /></div>
            <div><Label>Timezone</Label><Input value={form.timezone ?? "UTC"} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></div>
          </div>
        </Card>

        <PrivacyControls
          visibility={form.privacy_profile ?? "public"}
          precision={form.privacy_location ?? "exact"}
          onChange={(patch) => setForm({ ...form, ...patch })}
        />


        <Card className="space-y-5 p-6">
          <div>
            <h2 className="font-semibold">Notifications</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Choose what reaches you in Xamseen and by email. Security and account emails — verification, password
              resets and sign-in alerts — are always sent.
            </p>
          </div>

          <div className="grid grid-cols-[1fr_64px_64px] items-end gap-2 border-b pb-2 text-xs font-medium text-muted-foreground">
            <span>Category</span><span className="text-center">In app</span><span className="text-center">Email</span>
          </div>

          {NOTIFICATION_CATEGORIES.map((c) => (
            <div key={c.key} className="grid grid-cols-[1fr_64px_64px] items-center gap-2">
              <div>
                <Label className="text-sm">{c.label}</Label>
                <p className="text-xs text-muted-foreground">{c.hint}</p>
              </div>
              <div className="flex justify-center">
                <Switch
                  disabled={!prefs}
                  checked={!!prefs?.[`inapp_${c.key}` as const]}
                  onCheckedChange={(v) => setPrefs((p) => (p ? { ...p, [`inapp_${c.key}`]: v } : p))}
                />
              </div>
              <div className="flex justify-center">
                <Switch
                  disabled={!prefs}
                  checked={!!prefs?.[`email_${c.key}` as const]}
                  onCheckedChange={(v) => setPrefs((p) => (p ? { ...p, [`email_${c.key}`]: v } : p))}
                />
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between border-t pt-4">
            <div>
              <Label>Push notifications</Label>
              <p className="text-xs text-muted-foreground">Device push where supported.</p>
            </div>
            <Switch checked={!!form.notif_push} onCheckedChange={(v) => setForm({ ...form, notif_push: v })} />
          </div>
        </Card>


        <Card className="space-y-4 p-6">
          <div>
            <h2 className="font-semibold">Weekly civic digest</h2>
            <p className="mt-1 text-xs text-muted-foreground">Get a summary of new reports, resolutions and hotspots for your chosen area.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Scope</Label>
              <Select
                disabled={digestLoading}
                value={digest?.scope ?? "city"}
                onValueChange={(v) => setDigest((d) => d ? { ...d, scope: v as DigestScope } : { scope: v as DigestScope, frequency: "weekly" })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="neighborhood">My neighborhood</SelectItem>
                  <SelectItem value="city">My city</SelectItem>
                  <SelectItem value="country">My country</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frequency</Label>
              <Select
                disabled={digestLoading}
                value={digest?.frequency ?? "off"}
                onValueChange={(v) => setDigest((d) => d ? { ...d, frequency: v as DigestFrequency } : { scope: "city", frequency: v as DigestFrequency })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
        </div>

        <Card className="space-y-4 border-destructive/20 p-6">
          <div>
            <h2 className="font-semibold">Session</h2>
            <p className="mt-1 text-xs text-muted-foreground">Sign out of Xamseen on this device.</p>
          </div>
          <SignOutButton />
        </Card>
      </div>
    </AppShell>
  );
}

function SignOutButton() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate({ to: "/signin", replace: true });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not sign out. Please try again.");
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <Button variant="outline" onClick={handleSignOut} disabled={signingOut} className="w-full sm:w-auto">
      <LogOut className="mr-2 h-4 w-4" />
      {signingOut ? "Signing out…" : "Sign out"}
    </Button>
  );
}
